/**
 * Hand-built campaign links (the sales email sequences, LinkedIn posts, decks)
 * fill the UTM fields with the wrong things: the sequence step in utm_source
 * (`first-mail`), the email template in utm_medium (`finance-template1`), the
 * clicked CTA in utm_campaign (`book-appointment`). GA4 files a session under
 * Email / Organic Social / Referral by utm_medium alone, so those sessions
 * landed in "Unassigned" (~700 in six months). Sent emails keep their links,
 * so the location reported to GA4 is normalised here, without losing a value:
 * the step stays the source, the template becomes the campaign, the CTA the
 * content. Links that already set utm_content are left alone.
 */

// A sequence step: *-mail / *-email (first-mail, alternative-email,
// mail-source-in-cc), its abbreviation (a-m, f-m), a newsletter, a LinkedIn DM.
const STEP = /(^|-)e?mail($|-)|^[a-z]-m$|^newsletter|^linkedin-message$/i;

// A medium that names where the link sat rather than a channel: [source, medium].
const PLACEMENT: Record<string, [string, string]> = {
  linkedin: ["linkedin", "social"],
  deck: ["deck", "referral"],
};

// A value with stray whitespace or a trailing dot pasted with it (`linkedin.`).
const tidy = (v: string | null) => v?.trim().replace(/[.,;:!?]+$/, "") || null;

/** `search` with legacy UTMs moved onto GA4's vocabulary, or as is when there is nothing to move. */
export function normalizeCampaign(search: string): string {
  const q = new URLSearchParams(search);
  const source = tidy(q.get("utm_source"));
  if (!source || q.has("utm_content")) return search;
  const medium = tidy(q.get("utm_medium"));
  const campaign = tidy(q.get("utm_campaign"));

  const placed = medium ? PLACEMENT[medium.toLowerCase()] : undefined;
  let next: Record<string, string | null>;
  if (placed) {
    next = { utm_source: placed[0], utm_medium: placed[1], utm_campaign: source, utm_content: campaign };
  } else if (STEP.test(source) && !/^(email|social)$/i.test(medium ?? "")) {
    const channel = /^linkedin/i.test(source) ? "social" : "email";
    next = { utm_source: source, utm_medium: channel, utm_campaign: medium, utm_content: campaign };
  } else if (source !== q.get("utm_source")) {
    next = { utm_source: source };
  } else {
    return search;
  }

  for (const [key, value] of Object.entries(next)) {
    if (value) q.set(key, value);
    else q.delete(key);
  }
  return `?${q.toString()}`;
}
